import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { invalidateAttributionCache, invalidateCollectionCache } from '../cacheInvalidation';
import type {
    Collection,
    CreateCollectionData,
    UpdateCollectionData,
    PaginatedResponse,
    MoveLedgersToCollectionData,
} from '../types';

interface UseCollectionsParams {
    /**
     * Filter by collection type. When set, the backend returns every matching
     * collection (limit/offset are ignored server-side), so this is the way to
     * fetch a complete, unpaginated set rather than just the first page.
     */
    type?: Collection['type'];
}

export function useCollections(params: UseCollectionsParams = {}) {
    const query = params.type ? `?type=${encodeURIComponent(params.type)}` : '';
    return useQuery({
        queryKey: ['collections', params],
        queryFn: () => api.get<PaginatedResponse<Collection>>(`/api/collections${query}`),
    });
}

export function useCollection(id: number) {
    return useQuery({
        queryKey: ['collections', id],
        queryFn: () => api.get<Collection>(`/api/collections/${id}`),
        enabled: !!id,
    });
}

export function useCreateCollection() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateCollectionData) => api.post<Collection>('/api/collections', data),
        onSuccess: () => {
            invalidateCollectionCache(queryClient);
        },
    });
}

export function useUpdateCollection() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateCollectionData }) =>
            api.patch<Collection>(`/api/collections/${id}`, data),
        onSuccess: () => {
            invalidateCollectionCache(queryClient);
        },
    });
}

export function useDeleteCollection() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.delete<void>(`/api/collections/${id}`),
        onSuccess: (_, deletedId) => {
            queryClient.removeQueries({ queryKey: ['collections', deletedId] });
            invalidateCollectionCache(queryClient);
        },
    });
}

/**
 * Moves ledgers into a collection via POST /api/collections/{id}/ledgers.
 * Additive-move semantics: listed ledgers are moved into the collection,
 * unlisted ledgers are untouched (a ledger always belongs to a collection,
 * so removal is impossible). Idempotent for existing members.
 */
export function useMoveLedgersToCollection() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: MoveLedgersToCollectionData }) =>
            api.post<Collection>(`/api/collections/${id}/ledgers`, data),
        onSuccess: () => {
            invalidateAttributionCache(queryClient);
        },
    });
}

interface BatchLedgersData {
    ledgers: unknown;
    collections: PaginatedResponse<Collection>;
    tags: unknown;
    profile: unknown;
}

interface BatchCollectionsData {
    collections: PaginatedResponse<Collection>;
    profile: unknown;
}

/**
 * Helper to optimistically reorder collections in cache.
 */
function reorderCollectionsInCache(
    items: Collection[],
    id: number,
    newWeight: number
): Collection[] {
    const copy = [...items];
    const draggedIndex = copy.findIndex((c) => c.id === id);
    if (draggedIndex === -1) return items;

    // Remove dragged item from old position
    const [draggedItem] = copy.splice(draggedIndex, 1);
    if (!draggedItem) return items;

    // Insert at new position with updated weight
    const updatedDraggedItem: Collection = { ...draggedItem, weight: newWeight };
    copy.splice(newWeight, 0, updatedDraggedItem);

    // Update weights to match new order
    return copy.map((item, idx) => ({
        ...item,
        weight: idx,
    }));
}

/**
 * Optimistic reorder hook for drag-and-drop collection reordering.
 * Updates the cache immediately before the API call completes,
 * providing instant visual feedback without "snap back" effect.
 */
export function useReorderCollection() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, weight }: { id: number; weight: number }) =>
            api.patch<Collection>(`/api/collections/${id}`, { weight }),

        onMutate: async ({ id, weight }) => {
            // Cancel outgoing refetches to prevent race conditions
            await queryClient.cancelQueries({ queryKey: ['batch-ledgers'] });
            await queryClient.cancelQueries({ queryKey: ['batch-collections'] });

            // Snapshot previous values for rollback
            const previousBatchLedgers = queryClient.getQueriesData<BatchLedgersData>({
                queryKey: ['batch-ledgers'],
            });
            const previousBatchCollections = queryClient.getQueriesData<BatchCollectionsData>({
                queryKey: ['batch-collections'],
            });

            // Optimistically update batch-ledgers cache
            queryClient.setQueriesData<BatchLedgersData>(
                { queryKey: ['batch-ledgers'] },
                (old) => {
                    if (!old?.collections?.items) return old;
                    const updatedItems = reorderCollectionsInCache(old.collections.items, id, weight);
                    return {
                        ...old,
                        collections: { ...old.collections, items: updatedItems },
                    };
                }
            );

            // Optimistically update batch-collections cache
            queryClient.setQueriesData<BatchCollectionsData>(
                { queryKey: ['batch-collections'] },
                (old) => {
                    if (!old?.collections?.items) return old;
                    const updatedItems = reorderCollectionsInCache(old.collections.items, id, weight);
                    return {
                        ...old,
                        collections: { ...old.collections, items: updatedItems },
                    };
                }
            );

            return { previousBatchLedgers, previousBatchCollections };
        },

        onError: (_err, _vars, context) => {
            // Roll back on error
            if (context?.previousBatchLedgers) {
                context.previousBatchLedgers.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            if (context?.previousBatchCollections) {
                context.previousBatchCollections.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
        },

        onSettled: () => {
            // Refetch to get accurate weights from server
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-collections'] });
        },
    });
}
